import { Field, Form } from 'react-final-form'

import { sendApprovalEmail, Team } from 'common/api/teamsApi'
import Button from 'common/components/button'
import Checkbox from 'common/components/checkbox'
import Modal from 'common/components/Modal'
import { errorToast } from 'common/components/toastNotification'
import { useAppSelector } from 'common/hooks/redux'

import useFetchTeams from '../hooks/useFetchTeams'

import style from './SendForApprovalModal.module.css'

type Props = {
  isVisible: boolean
  campaignId?: string
  onClose: () => void
  onSendForApproval: (teams: Team[]) => void
}

const SendForApprovalModal = ({
  isVisible,
  campaignId,
  onClose,
  onSendForApproval,
}: Props) => {
  const accountId = useAppSelector((state) => state.authStates.accountId)

  const { data: teams } = useFetchTeams(accountId)

  const handleSendForApproval = async (values) => {
    if (campaignId) {
      try {
        const teamIds = Object.keys(values)
          .filter((key) => key.startsWith('team-checkbox-') && values[key])
          .map((key) => key.replace('team-checkbox-', ''))

        await sendApprovalEmail({ accountId, campaignId, teamslists: teamIds })

        const selectedTeams =
          teams?.filter((team) => teamIds.includes(team.id)) || []
        onSendForApproval(selectedTeams)
        onClose()
      } catch (e) {
        errorToast('Error sending approval email. Please try again later.')
      }
    }
  }

  return (
    <Form
      onSubmit={handleSendForApproval}
      render={({
        submitting: isSubmitting,
        pristine,
        values,
        handleSubmit,
      }) => {
        const hasValues = Object.values(values).some((value) => value)

        return (
          <Modal
            className={style.modal}
            maskClosable
            centered
            bodyStyle={{
              width: '800px',
              paddingLeft: '24px',
              paddingRight: '24px',
              paddingTop: '0px',
            }}
            data-testid="send-for-approval-modal"
            title={
              <h1 className="mx-2 my-2 text-2xl font-rob text-coolGray-800 flex items-center">
                Send for approval
              </h1>
            }
            visible={isVisible}
            onClose={onClose}
            footer={
              <div className="flex flex-row-reverse">
                <Button
                  className="ml-4"
                  data-testid="send-for-approval-button"
                  variant="primary"
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={pristine || !hasValues || isSubmitting}
                >
                  Send for approval
                </Button>

                <Button
                  data-testid="cancel-send-for-approval-button"
                  variant="link"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </div>
            }
          >
            {teams && teams.length > 0 ? (
              <>
                <div className="flex flex-col">
                  <span className="font-medium pb-6 text-coolGray-400">
                    Choose a team to review and approve this experiment variant.
                  </span>
                </div>
                <div className="flex gap-6 flex-wrap">
                  {teams.map((team) => {
                    return (
                      <div
                        key={team.id}
                        className="flex items-center whitespace-nowrap"
                      >
                        <Field<boolean> name={`team-checkbox-${team.id}`}>
                          {({ input }) => (
                            <Checkbox
                              data-testid={`team-checkbox-${team.id}`}
                              label={team.name}
                              isChecked={input.value}
                              onChange={input.onChange}
                            />
                          )}
                        </Field>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              <span className="font-medium pb-4 text-coolGray-400">
                There are no teams configured for this account.
              </span>
            )}
          </Modal>
        )
      }}
    ></Form>
  )
}

export default SendForApprovalModal
